'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Sparkles, Loader2 } from 'lucide-react';
import Link from 'next/link';

interface TrendingIcon {
  id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  follower_count: number;
  post_count: number;
}

export default function TrendingIcons() {
  const [icons, setIcons] = useState<TrendingIcon[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('profiles')
      .select('id, display_name, username, avatar_url, follower_count, post_count')
      .eq('role', 'icon')
      .eq('is_active', true)
      .order('follower_count', { ascending: false })
      .limit(5)
      .then(({ data }) => {
        setIcons((data as TrendingIcon[]) || []);
        setLoading(false);
      });
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-cream-300 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={15} className="text-gold-500" />
        <h3 className="text-sm font-semibold text-charcoal-900" style={{ fontFamily: "'Cinzel', serif" }}>Trending Icons</h3>
      </div>
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 size={18} className="animate-spin text-charcoal-400" />
        </div>
      ) : icons.length === 0 ? (
        <p className="text-xs text-charcoal-400 text-center py-4">No icons yet. Be the first!</p>
      ) : (
        <div className="space-y-3">
          {icons.map((icon) => (
            <Link key={icon.id} href={`/profile/${icon.username || icon.id}`}
              className="flex items-center gap-3 group">
              {icon.avatar_url ? (
                <img src={icon.avatar_url} alt={icon.display_name || ''} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-300 to-gold-500 flex items-center justify-center text-xs font-bold text-white shrink-0">
                  {icon.display_name?.charAt(0).toUpperCase() || '?'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-charcoal-900 group-hover:text-gold-600 transition-colors truncate">
                  {icon.display_name || icon.username}
                </p>
                <p className="text-xs text-charcoal-400">{icon.follower_count.toLocaleString()} followers</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}